import { FC, ReactNode, Suspense, lazy, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material'
import { Dayjs, ManipulateType } from 'dayjs'
import { DateRange } from '@mui/x-date-pickers-pro'
import { LoadedPageRightTitle, LoadedPageLeftTitle } from '../../display'
import { LoadedCalendarReportFilter } from '../../inputs'
import { RangeFilter } from '$constants'
import useAppStore from '$appStore'
import { useShallow } from 'zustand/react/shallow'
import { useLocation, useNavigate, useRevalidator } from 'react-router-dom'
import {
  isMobile,
  toCompatibleSearchString,
  toUrlString,
  withUrlSearchParams,
} from '$utils'
import { useCompanySettings } from '$hooks'
import { getDateRangeText, shiftReportRange } from '../../helpers'
import { reportRangeSearchParams } from '../../helpers/queryString'

type PageHeaderProps = {
  title: string
  displayLeftTitle?: boolean
  displayDateFilter?: boolean
  customBottomLeftComponent?: string
  /** Optional element rendered to the right of the title (e.g. a help icon). */
  titleAction?: ReactNode
  children?: ReactNode
}

const PageHeader: FC<PageHeaderProps> = ({
  title,
  displayLeftTitle = true,
  displayDateFilter = displayLeftTitle,
  customBottomLeftComponent,
  titleAction,
  children,
}) => {
  // Hooks
  const theme = useTheme<Theme>()
  const revalidator = useRevalidator()
  const navigate = useNavigate()
  const { search: currentSearch } = useLocation()
  const { weekStartPreference, dayStartPreference } = useCompanySettings()

  // Remote states useAppStore
  const [
    setReportsMetadata,
    { startDateTime, endDateTime, dateRange, prevDateRange },
  ] = useAppStore(
    useShallow((state) => [state.setReportsMetadata, state.reportsMetadata])
  )

  // Local state
  const [displayCalendar, setDisplayCalendar] = useState<boolean>(false)

  // From the store every render: the header is not re-mounted between routes, and a loader
  // or a deep link can set the range
  const dateRangeText = getDateRangeText(
    startDateTime,
    endDateTime,
    dateRange,
    dayStartPreference
  )

  // A custom range opens the calendar on the period it came from
  const prevRangeForCalendar =
    dateRange === RangeFilter.Custom ? prevDateRange : dateRange

  const breadcrumButtonSx: SxProps<Theme> = {
    fontSize: '.7em',
    color: theme.palette.primary.dark,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: `underline ${theme.palette.primary.dark}`,
    },
  }

  /* The url holds the range so a refresh, a shared link and the sidenav menu all find it, and
  the revalidator is what reloads the page and shows the loading bar. */
  const applyRange = (
    start: Dayjs,
    end: Dayjs,
    rangeFilter: RangeFilter,
    prevRangeFilter: RangeFilter
  ) => {
    setDisplayCalendar(false)
    setReportsMetadata(start, end, rangeFilter, prevRangeFilter)

    // Merged with the url so values the router did not write, like the tab, survive
    const nextSearchParams = withUrlSearchParams(
      reportRangeSearchParams({
        startDateTime: start,
        endDateTime: end,
        dateRange: rangeFilter,
        prevDateRange: prevRangeFilter,
      })
    )

    /* AngularJS-compatible serialization instead of setSearchParams: setSearchParams encodes
    colons as %3A, AngularJS recomposes them literal, and on the byte mismatch it pushes its own
    copy of the url onto the history stack, eating browser back presses. */
    const nextSearch = toCompatibleSearchString(nextSearchParams)

    // Applying the range already on screen should not stack another history entry
    if (`?${nextSearch}` !== currentSearch) {
      // Same event a route change emits, so AngularJS syncs its $location
      window.dispatchEvent(
        new CustomEvent('@@loaded/reactNavigation', {
          detail: {
            to: toUrlString({ search: `?${nextSearch}` }),
            reactInternal: true,
            sameRoute: true,
          },
        })
      )
      navigate({ search: nextSearch })
    }

    revalidator.revalidate()
  }

  const onClickDateRange = (value: number, unit: ManipulateType) => {
    const { start, end } = shiftReportRange(
      startDateTime,
      endDateTime,
      value,
      unit,
      dateRange,
      prevDateRange,
      weekStartPreference
    )

    applyRange(start, end, dateRange, prevDateRange)
  }

  const handleOnApply = (
    pickedRange: DateRange<Dayjs>,
    rangeFilter: RangeFilter,
    prevRangeFilter: RangeFilter
  ) => {
    // No dates picked means only the kind of period changed, so the range on screen stays.
    // Still goes through applyRange: the url has to hold every range the store holds, or the
    // next load reads the url, finds nothing and drops back to the default.
    const [start, end] =
      pickedRange?.[0] && pickedRange[1]
        ? [pickedRange[0], pickedRange[1]]
        : [startDateTime, endDateTime]

    applyRange(start, end, rangeFilter, prevRangeFilter)
  }

  const handleSelectClick = () => setDisplayCalendar((prev) => !prev)

  /* This type needs to be any because React.LazyExoticComponent<React.ComponentType<any>> does not 
    allow us to reutrn <></> and we would end up having import(customBottomLeftComponent || '')) without
    avoiding the import function if the customBottomLeftComponent is not defined */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomBottomLeftComponent: any = customBottomLeftComponent ? (
    lazy(
      () =>
        import(`../custom-header-components/${customBottomLeftComponent}.tsx`)
    )
  ) : (
    <></>
  )

  const stackDirection = isMobile() ? 'column' : 'row'

  const selectButton = (
    <Button
      disableRipple
      sx={{ ...breadcrumButtonSx }}
      onClick={handleSelectClick}
    >
      SELECT
    </Button>
  )

  return (
    <Paper
      sx={{
        ...(!displayCalendar && displayDateFilter && { paddingBottom: '.2em' }),
        backgroundColor: 'rgba(0,0,0,0)',
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      <Stack direction={stackDirection} justifyContent={'space-between'}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LoadedPageRightTitle text={title} />
            {titleAction}
          </Stack>
          {customBottomLeftComponent && (
            <Suspense fallback={<CircularProgress size={15} />}>
              <CustomBottomLeftComponent />
            </Suspense>
          )}
        </Stack>
        <Box>
          {displayLeftTitle && <LoadedPageLeftTitle text={dateRangeText} />}
          {displayDateFilter && (
            <Stack
              direction={'row'}
              sx={{
                justifyContent: 'flex-end',
                ...(displayCalendar && { paddingBottom: '1em' }),
                displayPrint: 'none',
                ...(isMobile() && {
                  paddingTop: '8px',
                  paddingBottom: '16px',
                  justifyContent: 'space-around',
                  fontSize: '1.1em',
                }),
              }}
            >
              {(dateRange === RangeFilter.Day ||
                (dateRange === RangeFilter.Custom &&
                  prevDateRange === RangeFilter.Day)) && (
                <Box>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'week')}
                  >
                    {'<< WEEK'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'day')}
                  >
                    {'< DAY'}
                  </Button>
                  {selectButton}
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'day')}
                  >
                    {'DAY >'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'week')}
                  >
                    {'WEEK >>'}
                  </Button>
                </Box>
              )}

              {(dateRange === RangeFilter.Week ||
                (dateRange === RangeFilter.Custom &&
                  prevDateRange === RangeFilter.Week)) && (
                <Box>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'month')}
                  >
                    {'<< MONTH'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'week')}
                  >
                    {'< WEEK'}
                  </Button>
                  {selectButton}
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'week')}
                  >
                    {'WEEK >'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'month')}
                  >
                    {'MONTH >>'}
                  </Button>
                </Box>
              )}

              {(dateRange === RangeFilter.Month ||
                (dateRange === RangeFilter.Custom &&
                  prevDateRange === RangeFilter.Month)) && (
                <Box>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'year')}
                  >
                    {'<< YEAR'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'month')}
                  >
                    {'< MONTH'}
                  </Button>
                  {selectButton}
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'month')}
                  >
                    {'MONTH >'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'year')}
                  >
                    {'YEAR >>'}
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Stack>

      {displayDateFilter && displayCalendar && (
        <LoadedCalendarReportFilter
          dateRange={[startDateTime, endDateTime]}
          dateRangeFilter={dateRange}
          prevDateRangeFilter={prevRangeForCalendar}
          onCancel={() => setDisplayCalendar(false)}
          onApply={handleOnApply}
        />
      )}

      {children && <Box sx={{ width: '100%', marginTop: 3 }}>{children}</Box>}
    </Paper>
  )
}

export default PageHeader
